import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useAuth } from '../context/AuthContext';

function CallLogs() {
  const { currentUser } = useAuth();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Check if current user is admin (to show filter) or merchant (to auto-filter)
  const isAdmin = currentUser?.role === 'admin';
  const isMerchant = currentUser?.role === 'merchant';
  const userMerchantId = currentUser?.merchant_id;

  const fetchMerchants = async () => {
    try {
      const response = await axios.get('/admin/merchants');
      setMerchants(response.data.merchants || []);
    } catch (err) {
      console.error('Error fetching merchants:', err);
    }
  };

  const fetchCallLogs = async () => {
    try {
      const response = await axios.get('/twilio/call-logs');
      const callLogs = response.data.logs || [];
      console.log('Call logs data received:', callLogs); // Debug log
      setLogs(callLogs);
      
      // Auto-filter for merchants
      if (isMerchant && userMerchantId) {
        console.log('Filtering for merchant:', userMerchantId); // Debug log
        const merchantLogs = callLogs.filter(log => {
          console.log('Log merchant_id:', log.merchant_id, 'User merchant_id:', userMerchantId); // Debug log
          return log.merchant_id === userMerchantId;
        });
        console.log('Filtered logs:', merchantLogs); // Debug log
        setFilteredLogs(merchantLogs);
      } else {
        setFilteredLogs(callLogs);
      }
      
      setLoading(false);
    } catch (err) {
      setError('Failed to load call logs');
      setLoading(false);
      console.error('Call logs fetch error:', err);
    }
  };

  const getMerchantName = (merchant_id, merchant_name) => {
    // Use merchant_name from the API response if available
    if (merchant_name) {
      return merchant_name;
    }
    // Fallback to looking up in merchants array (for backward compatibility)
    const merchant = merchants.find(m => m.merchant_id === merchant_id);
    return merchant ? merchant.name : 'Unknown Merchant';
  };

  useEffect(() => {
    if (isAdmin) {
      fetchMerchants(); // Only fetch merchants for admin
    }
    fetchCallLogs();
  }, [currentUser]);

  // Filter logs by merchant (only for admin)
  useEffect(() => {
    if (isAdmin) {
      if (selectedMerchant === '') {
        setFilteredLogs(logs);
      } else {
        const filtered = logs.filter(log => 
          log.merchant_id === selectedMerchant
        );
        setFilteredLogs(filtered);
      }
    }
  }, [selectedMerchant, logs, isAdmin]);

  const handleMerchantFilter = (event) => {
    setSelectedMerchant(event.target.value);
    setPage(0); // Reset to first page when filtering
  };

  const clearFilter = () => {
    setSelectedMerchant('');
    setPage(0); // Reset to first page when clearing filter
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page data
  const paginatedLogs = filteredLogs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'busy':
        return 'warning';
      case 'no-answer':
        return 'warning';
      case 'in-progress':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Call Logs {isMerchant && currentUser?.merchant_name ? `(${currentUser.merchant_name})` : ''}
        </Typography>
      </Box>

      {/* Filter Section - Only show for admin */}
      {isAdmin && (
        <Box display="flex" alignItems="center" gap={2} mb={3} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 2 }}>
          <FilterListIcon color="action" />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Merchant</InputLabel>
            <Select
              value={selectedMerchant}
              label="Filter by Merchant"
              onChange={handleMerchantFilter}
            >
              <MenuItem value="">All Merchants</MenuItem>
              {merchants.map((merchant) => (
                <MenuItem key={merchant.merchant_id} value={merchant.merchant_id}>
                  {merchant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedMerchant && (
            <Chip 
              label={`Filtered: ${getMerchantName(selectedMerchant, null)}`}
              onDelete={clearFilter}
              color="primary"
              variant="outlined"
            />
          )}
          <Typography variant="body2" color="textSecondary">
            Showing {filteredLogs.length} of {logs.length} logs
          </Typography>
        </Box>
      )}

      {/* Show count info for merchants */}
      {isMerchant && (
        <Box mb={3} p={2} sx={{ backgroundColor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Showing {filteredLogs.length} call logs for your merchant account
          </Typography>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="call logs table">
          <TableHead>
            <TableRow>
              <TableCell>SID</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Date/Time</TableCell>
              {/* Only show Merchant column for admin */}
              {isAdmin && <TableCell>Merchant</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedLogs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>{log.call_sid}</TableCell>
                <TableCell>{log.from_number}</TableCell>
                <TableCell>{log.to_number}</TableCell>
                <TableCell>
                  <Chip 
                    label={log.direction || 'unknown'} 
                    color={log.direction === 'inbound' ? 'primary' : 'secondary'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={log.call_status} 
                    color={getStatusColor(log.call_status)} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>{log.duration} sec</TableCell>
                <TableCell>{new Date(log.call_date).toLocaleString()}</TableCell>
                {/* Only show Merchant cell for admin */}
                {isAdmin && (
                  <TableCell>
                    <Chip 
                      label={getMerchantName(log.merchant_id, log.merchant_name)}
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
            {filteredLogs.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7} align="center">No call logs found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredLogs.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}

export default CallLogs;
